// Prepack Recipe Model & Validation Helpers
// Manages organization-owned Prepack Recipe schemas, strategy constraints,
// odds transformations, lossless JSON roundtrips, and inline form validation.

import { americanToDecimal as decimalFromAmerican, formatAmericanOdds as formatSignedOdds } from '@/utils/odds'
import { formatSportLabel, startWord } from '@/utils/sportLabels'
import { cleanLegMix } from './prepackLegMix'
import { legMixProblems, supportsLegMix, withLegMix } from './builder/legMix'
import leagueAliases from '../../../../../shared/pkg/prepacks/leagueAliases.json'
import leagueGroups from '../../../../../shared/pkg/prepacks/leagueGroups.json'

const RECIPE_WORD_SEPARATOR_PATTERN = /[\s_-]+/
const UNSIGNED_INTEGER_PATTERN = /^\d+$/
const WHITESPACE_OR_HYPHEN_RUN_PATTERN = /[\s-]+/g
const COMBINING_MARK_PATTERN = /\p{M}/gu
const WHITESPACE_RUN_PATTERN = /\s+/g
const NON_ALPHANUMERIC_PATTERN = /[^a-z0-9]/g

export const ALL_LEG_COUNTS = [2, 3, 4, 5, 6]
// One ordered sport list for the Library, schedules, and builder. Keep this
// in parity with Go's prepacks.LibrarySports.
export const PREPACK_SPORT_IDS = Object.freeze(['football', 'basketball', 'baseball', 'hockey', 'soccer', 'rugby', 'motorsports', 'mma', 'golf', 'tennis', 'cricket'])
// The sports a book's operators reach for first, by Kambi offering id (the
// schedule's regulation). Sunbet (siwc) builds mostly soccer, rugby and
// cricket packs (#840). An offering not listed here keeps the order above.
// Here, not in the builder, so the Surfaces page's filters (#1142) follow
// the builder's order without loading the builder.
export const FEATURED_SPORTS_BY_OFFERING = Object.freeze({
  siwc: Object.freeze(['soccer', 'rugby', 'cricket']),
})

/**
 * PREPACK_SPORT_IDS as an offering's operators see them: its featured sports
 * first, in their listed order, then every other sport in order. An empty,
 * unknown or unfeatured offering returns PREPACK_SPORT_IDS itself.
 */
export function prepackSportIdsFor(offering = '') {
  const featured = (FEATURED_SPORTS_BY_OFFERING[String(offering || '').trim().toLowerCase()] || []).filter((id) => PREPACK_SPORT_IDS.includes(id))
  if (featured.length === 0) return PREPACK_SPORT_IDS
  return [...featured, ...PREPACK_SPORT_IDS.filter((id) => !featured.includes(id))]
}

// The builder's leagues per sport, in its order, by the names the server's
// league table uses (rules.leaguesBySport). The rugby, motorsports, MMA, golf
// and tennis names are the prototype's (#688); generation.go says which of
// them Kambi uses as event groups, and formatLeagueLabel names them as
// leagueGroups.json does ("Six Nations" for SIX_NATIONS). College football is
// Kambi's NCAAF; CFB is an alias of it (leagueAliases.json), not a second
// pick. Formula 1 and NASCAR are Kambi sports, not groups, so they are sport
// aliases below, not leagues. The soccer leagues after MLS are Sunbet's
// (#829); leagueGroups.json names them and ties Liga Portugal, Saudi Pro
// League and Süper Lig to Kambi's "Primeira Liga", "Professional League" and
// "Süper Lig".
export const CANONICAL_LEAGUE_TO_SPORT = {
  NFL: 'football',
  NCAAF: 'football',
  CFL: 'football',
  MLB: 'baseball',
  KBO: 'baseball',
  NPB: 'baseball',
  NBA: 'basketball',
  WNBA: 'basketball',
  NCAAB: 'basketball',
  NHL: 'hockey',
  EPL: 'soccer',
  MLS: 'soccer',
  PSL: 'soccer',
  LA_LIGA: 'soccer',
  SERIE_A: 'soccer',
  BUNDESLIGA: 'soccer',
  LIGUE_1: 'soccer',
  LIGA_PORTUGAL: 'soccer',
  EREDIVISIE: 'soccer',
  SAUDI_PRO_LEAGUE: 'soccer',
  SUPER_LIG: 'soccer',
  SIX_NATIONS: 'rugby',
  SUPER_RUGBY: 'rugby',
  PREMIERSHIP_RUGBY: 'rugby',
  NRL: 'rugby',
  RUGBY_WORLD_CUP: 'rugby',
  INDYCAR: 'motorsports',
  MOTOGP: 'motorsports',
  UFC: 'mma',
  PFL: 'mma',
  BELLATOR: 'mma',
  ONE_CHAMPIONSHIP: 'mma',
  PGA: 'golf',
  LPGA: 'golf',
  DP_WORLD_TOUR: 'golf',
  LIV: 'golf',
  ATP: 'tennis',
  WTA: 'tennis',
  GRAND_SLAMS: 'tennis',
  ITF: 'tennis',
  SA20: 'cricket',
  BIG_BASH: 'cricket',
}

// Twin of the Go league table used by in-memory schedule and pack matching.
// The provider group spellings come from the same leagueGroups.json that Go's
// EventLeagueID reads (for example KBO League and Professional Fighters
// League), while the table ids and aliases cover the builder's league keys.
const leagueLookupToken = (value) => String(value ?? '').trim().toLowerCase().split(RECIPE_WORD_SEPARATOR_PATTERN).filter(Boolean).join('_').toUpperCase()
const LEAGUE_TABLE_EXTRAS = {
  UCL: 'soccer',
  AAA: 'baseball',
  AHL: 'hockey',
  KHL: 'hockey',
  SHL: 'hockey',
}
const LEAGUE_TABLE_SPORTS = { ...CANONICAL_LEAGUE_TO_SPORT, ...LEAGUE_TABLE_EXTRAS }
const GROUP_LEAGUE_SPORT_IDS = Object.fromEntries(Object.entries(leagueGroups).flatMap(([id, row]) => {
  const names = [id, row.label, row.parent, ...(row.groups || []).map((group) => group.name)]
  return names.filter(Boolean).map((name) => [leagueLookupToken(name), row.sport])
}))
const ALIAS_LEAGUE_SPORT_IDS = Object.fromEntries(Object.entries(leagueAliases).flatMap(([alias, id]) => {
  const sport = LEAGUE_TABLE_SPORTS[id]
  return sport ? [[leagueLookupToken(alias), sport]] : []
}))
export const LEAGUE_SPORT_IDS = Object.freeze({
  ...LEAGUE_TABLE_SPORTS,
  ...GROUP_LEAGUE_SPORT_IDS,
  ...ALIAS_LEAGUE_SPORT_IDS,
})

// Sport names and spellings beside the leagues: CANONICAL_LEAGUE_TO_SPORT
// owns which sport each league belongs to. Kambi's own sport ids
// (RUGBY_UNION, MARTIAL_ARTS, ...) read as the builder's sports, as the
// server's SportKey reads them.
export const SPORT_ALIASES = {
  ...CANONICAL_LEAGUE_TO_SPORT,
  FOOTBALL: 'football',
  AMERICAN_FOOTBALL: 'football',
  AMERICANFOOTBALL: 'football',
  // No longer a picker league (leagueKey reads it as NCAAF); a sport saved
  // as CFB still reads as football, as the server's NormalizeScope reads it.
  CFB: 'football',
  BASEBALL: 'baseball',
  BASKETBALL: 'basketball',
  HOCKEY: 'hockey',
  ICE_HOCKEY: 'hockey',
  SOCCER: 'soccer',
  FOOTBALL_SOCCER: 'soccer',
  RUGBY: 'rugby',
  RUGBY_UNION: 'rugby',
  RUGBY_LEAGUE: 'rugby',
  MOTORSPORTS: 'motorsports',
  MOTORSPORT: 'motorsports',
  MOTOR_SPORTS: 'motorsports',
  MOTOR_RACING: 'motorsports',
  // Kambi's racing sports (rules.SportKey): series and races, not leagues.
  FORMULA_1: 'motorsports',
  FORMULA_ONE: 'motorsports',
  F1: 'motorsports',
  // The server's leagueSport reads these league spellings as their sport
  // too (knownLeagueToSport in recipes.go); the Straight-only check needs them.
  FORMULA1: 'motorsports',
  NASCAR_CUP_SERIES: 'motorsports',
  NASCAR: 'motorsports',
  INDY_CAR: 'motorsports',
  MOTORCYCLING: 'motorsports',
  MMA: 'mma',
  MARTIAL_ARTS: 'mma',
  MIXED_MARTIAL_ARTS: 'mma',
  UFC_MMA: 'mma',
  GOLF: 'golf',
  EUROPEAN_TOUR: 'golf',
  TENNIS: 'tennis',
}

/**
 * The form two league names are compared in, as the server's leagueKey
 * writes it: the table's name for a known spelling ("Premier League" and
 * "EPL" are both EPL; leagueAliases.json is the server's list), else upper
 * case with each run of spaces, hyphens and underscores as one underscore,
 * so an availability answer's "SIX NATIONS" is the builder's SIX_NATIONS.
 * An explicit sport also ties a provider group to the builder league
 * leagueGroups.json files it under, as the server's EventLeagueID does:
 * "Premiership" is PREMIERSHIP_RUGBY only when the sport is rugby, "Rugby
 * World Cup 2027" is RUGBY_WORLD_CUP. Parent leagues (GRAND_SLAMS) never
 * rename a group; availability answers name a group's parents.
 */
export function leagueKey(value, sport = '') {
  const token = String(value ?? '').toLowerCase().split(RECIPE_WORD_SEPARATOR_PATTERN).filter(Boolean).join('_')
  const key = Object.hasOwn(leagueAliases, token) ? leagueAliases[token] : token.toUpperCase()
  for (const [id, row] of Object.entries(leagueGroups)) {
    if (canonicalSport(sport) !== row.sport) continue
    if ((row.groups || []).some((group) => isLeagueGroup(group, row, key, sport))) return id
  }
  return key
}

/**
 * The name the builder shows for a league id: its leagueGroups.json row's
 * label ("Six Nations" for SIX_NATIONS, the name the server's LeagueLabel
 * gives availability rows), else the Kambi group a parent row stands for
 * ("PGA Tour"), else a builder league's id ("NFL"), else the league as it
 * was written. Saved values and searches keep the id.
 */
export function formatLeagueLabel(league) {
  const id = leagueKey(league)
  const row = Object.hasOwn(leagueGroups, id) ? leagueGroups[id] : null
  if (row) return row.label || row.parent || id
  // A builder league reads as its id ("NCAAF" for a saved "cfb", "EPL" for
  // "Premier League"); any other league as it was written ("Bundesliga 2"),
  // never in its key form.
  return Object.hasOwn(CANONICAL_LEAGUE_TO_SPORT, id) ? id : String(league ?? '').trim() || id
}

// Whether a league key is one of a leagueGroups.json row's groups in the
// sport, as the server's leagueGroupName.matches reads it: the group's name,
// or with `yearly` its name and a year ("RUGBY_WORLD_CUP_2027"), and for a
// group with a `kambi_sport` only under that Kambi sport (RUGBY_LEAGUE) or
// the bare builder sport (rugby).
function isLeagueGroup(group, row, key, sport) {
  if (group.kambi_sport) {
    const token = String(sport ?? '').toUpperCase().split(RECIPE_WORD_SEPARATOR_PATTERN).filter(Boolean).join('_')
    if (token !== group.kambi_sport && token !== row.sport.toUpperCase()) return false
  }
  const name = leagueKey(group.name)
  if (key === name) return true
  const year = key.startsWith(`${name}_`) ? key.slice(name.length + 1) : ''
  return Boolean(group.yearly) && UNSIGNED_INTEGER_PATTERN.test(year)
}

/** The canonical sport token (`football`, `soccer`, ...) for a sport or league name. */
export function canonicalSport(value) {
  const token = String(value || '').trim().toUpperCase()
  return SPORT_ALIASES[token] || String(value || '').trim().toLowerCase()
}

export function leagueSportId(league) {
  return LEAGUE_SPORT_IDS[leagueKey(league)] || null
}

export function packSportId(sport, league) {
	const raw = String(sport || '').trim().toLowerCase().replace(WHITESPACE_OR_HYPHEN_RUN_PATTERN, '_')
	const fromLeague = leagueSportId(league)
	if (!raw || raw === 'general') return fromLeague || ''
	// Kambi FOOTBALL is soccer; legacy Studio football rows with an
	// American league remain Football.
	if (raw === 'football') return fromLeague === 'football' ? 'football' : 'soccer'
	const token = canonicalSport(raw)
	if (PREPACK_SPORT_IDS.includes(token)) return token
	return fromLeague || token
}

function normalizedValues(values) {
  return (Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)
}

/**
 * What a schedule calls the start of its games (`startWord`): the
 * definition's sports plus its leagues' sports; with neither, the sports its
 * pack type pins (Home run props: "first pitch"). `RecipeStartWord` in
 * apps/shared/pkg/prepacks applies the same rule to the same table.
 */
export function scheduleStartWord(definition = {}, descriptor = null) {
  return startWord(definition?.sports, definition?.leagues, descriptor?.sports)
}

function optionScope(option) {
  const sports = normalizedValues(option?.sports).map(canonicalSport)
  const leagues = normalizedValues(option?.leagues).map((league) => leagueKey(league, sports.length === 1 ? sports[0] : ''))
  return { leagues, sports }
}

/**
 * The recipe's effective sports: the canonical sports the explicit sports,
 * the leagues' sports and the strategy's sports agree on (each that is set).
 * `restricted` is false when none is set; an empty `sports` with
 * `restricted` true is a contradictory scope.
 */
export function marketScopeSports({ leagues = [], sports = [], descriptor = null } = {}) {
  const explicitSports = normalizedValues(sports).map(canonicalSport)
  const activeLeagues = normalizedValues(leagues).map((league) => leagueKey(league, explicitSports.length === 1 ? explicitSports[0] : ''))
  const strategySports = normalizedValues(descriptor?.sports).map(canonicalSport)
  // Leagues restrict the sports only when the builder knows every one, as
  // the server reads them: a live pick of a Kambi group it does not know
  // (WIMBLEDON) may be any sport's.
  const leagueSports = activeLeagues.map((league) => CANONICAL_LEAGUE_TO_SPORT[league])
  const knownLeagueSports = leagueSports.every(Boolean) ? leagueSports : []
  const restrictions = [
    new Set(explicitSports),
    new Set(knownLeagueSports),
    new Set(strategySports),
  ].filter((restriction) => restriction.size > 0)
  const scoped = restrictions.length === 0
    ? new Set()
    : restrictions.slice(1).reduce(
      (intersection, restriction) => new Set(
        [...intersection].filter((sport) => restriction.has(sport)),
      ),
      restrictions[0],
    )
  return { sports: scoped, restricted: restrictions.length > 0, activeLeagues }
}

/**
 * Returns descriptors compatible with the recipe's effective sport scope.
 * An empty recipe scope is unrestricted unless the strategy itself is scoped.
 */
export function filterOptionsByScope(options = [], { leagues = [], sports = [], descriptor = null } = {}) {
  if (!Array.isArray(options)) return []

  const { sports: scopedSports, restricted: hasScopeRestriction, activeLeagues } = marketScopeSports({ leagues, sports, descriptor })
  const impossibleScope = hasScopeRestriction && scopedSports.size === 0

  return options.filter((option) => {
    if (!option || typeof option !== 'object') return false
    const { leagues: optionLeagues, sports: optionSports } = optionScope(option)

    // A contradictory recipe/strategy scope must not make every scoped
    // descriptor available. Truly unrestricted descriptors remain usable so
    // callers can still preserve generic saved values.
    if (impossibleScope) return optionLeagues.length === 0 && optionSports.length === 0
    if (!hasScopeRestriction) return true

    if (activeLeagues.length > 0 && optionLeagues.length > 0) {
      const matchesLeague = optionLeagues.some((league) => activeLeagues.includes(league))
      if (!matchesLeague) return false
    }
    if (optionLeagues.length > 0) {
      const matchesSport = optionLeagues.some((league) => {
        const sport = CANONICAL_LEAGUE_TO_SPORT[league]
        return sport ? scopedSports.has(sport) : false
      })
      if (matchesSport) return true
    }
    if (optionSports.length > 0) return optionSports.some((sport) => scopedSports.has(sport))
    return optionLeagues.length === 0
  })
}

/** "A", "A and B", "A, B and C". */
export function joinWords(words) {
  if (words.length <= 1) return words.join('')
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

/** The schedule's bet type: the definition's own, else the pack type's (lower case, '' when neither says). */
export function scheduleBetType(definition, descriptor) {
  const own = String(definition?.bet_type ?? '').trim().toLocaleLowerCase()
  return own || String(descriptor?.bet_type ?? '').trim().toLocaleLowerCase()
}

/** Whether a pack type builds Straight packs: its `bet_type`, or its id from a server that sends none. */
export function isStraightPackType(descriptor) {
  const betType = scheduleBetType(null, descriptor)
  return betType ? betType === 'straight' : descriptor?.id === 'straight'
}

/** The catalog's Straight pack type, or null when it lists none. */
export function straightPackType(strategies = []) {
  return (Array.isArray(strategies) ? strategies : []).find((strategy) => strategy && isStraightPackType(strategy)) || null
}

/**
 * The bet types each sport is limited to, as the server advertises them
 * (`filter_options.sport_bet_types`, `{ golf: ['straight'] }`); a sport it
 * leaves out offers every bet type. {} from an older server: no limits.
 */
export function sportBetTypes(filterOptions) {
  const limits = filterOptions?.sport_bet_types
  if (!limits || typeof limits !== 'object' || Array.isArray(limits)) return {}
  return Object.fromEntries(Object.entries(limits)
    .filter(([, betTypes]) => Array.isArray(betTypes))
    .map(([sport, betTypes]) => [canonicalSport(sport), betTypes.map((betType) => String(betType ?? '').trim().toLocaleLowerCase()).filter(Boolean)]))
}

// The builder sport a sport or league token names, as the server's
// leagueSport reads both: `PGA Tour` and `golf` are golf, `Formula 1` motorsports.
const tokenSport = (token) => canonicalSport(leagueKey(token))

/**
 * The sports a scope names, as a sport or by one of its leagues, that offer
 * Straight packs only (`sportBetTypes`), sorted, whatever pack type the
 * schedule has: golf beside football still limits it. The server's
 * StraightOnlySportsIn applies the same rule.
 */
export function straightOnlySportsFor(sports = [], leagues = [], filterOptions = null) {
  const limits = sportBetTypes(filterOptions)
  const scope = new Set([...normalizedValues(sports), ...normalizedValues(leagues)].map(tokenSport))
  return Object.keys(limits)
    .filter((sport) => scope.has(sport) && limits[sport].length > 0 && limits[sport].every((betType) => betType === 'straight'))
    .sort()
}

/** The Straight-only sports the definition names while its pack type builds other packs; [] without a pack type. */
export function straightOnlySports(definition = {}, descriptor = null, filterOptions = null) {
  if (!descriptor) return []
  const betType = scheduleBetType(definition, descriptor)
  if (betType ? betType === 'straight' : descriptor.id === 'straight') return []
  return straightOnlySportsFor(definition?.sports, definition?.leagues, filterOptions)
}

/** "Golf offers Straight packs only", "Golf and Motorsports offer Straight packs only". */
export function straightOnlyPhrase(sports = []) {
  return `${joinWords(sports.map(formatSportLabel))} ${sports.length === 1 ? 'offers' : 'offer'} Straight packs only`
}

/** Why the pack type and the Straight-only sports cannot go together, with both fixes. */
export function straightOnlyReason(sports = []) {
  return `${straightOnlyPhrase(sports)}. Choose the Straight pack type or remove ${joinWords(sports.map(formatSportLabel))}.`
}

/** The definition's sports and leagues without those that name `sportIds`, read as straightOnlySportsFor reads them. */
export function scopeWithoutSports(definition = {}, sportIds = []) {
  const drop = new Set(sportIds)
  const keep = (values) => (Array.isArray(values) ? values : []).filter((value) => !drop.has(tokenSport(value)))
  return { sports: keep(definition?.sports), leagues: keep(definition?.leagues) }
}

/**
 * Validates that there are no direct include/exclude conflicts in market families or participants.
 */
export function getFilterConflicts(filters = {}) {
  const conflicts = []
  const intersectingValues = (left, right) => {
    const rightValues = new Set(right.map((value) => String(value).trim().toLocaleLowerCase()))
    return left.filter((value) => rightValues.has(String(value).trim().toLocaleLowerCase()))
  }
  const mf = Array.isArray(filters.market_families) ? filters.market_families : []
  const emf = Array.isArray(filters.excluded_market_families) ? filters.excluded_market_families : []
  const mfIntersect = intersectingValues(mf, emf)
  if (mfIntersect.length > 0) {
    conflicts.push(`Market family cannot be both included and excluded: ${mfIntersect.join(', ')}`)
  }

  const p = Array.isArray(filters.participants) ? filters.participants : []
  const ep = Array.isArray(filters.excluded_participants) ? filters.excluded_participants : []
  const pIntersect = intersectingValues(p, ep)
  if (pIntersect.length > 0) {
    conflicts.push(`Participant cannot be both included and excluded: ${pIntersect.join(', ')}`)
  }

  return conflicts
}



export const KNOWN_DEFINITION_KEYS = [
  'version',
  'strategy',
  'bet_type',
  'sports',
  'leagues',
  'leg_counts',
  'min_odds',
  'max_odds',
  'min_leg_odds',
  'max_leg_odds',
  'event_lookahead_hours',
  'start_time_after',
  'start_time_before',
  'filters',
]

export const KNOWN_FILTER_KEYS = [
  'market_families',
  'market_keys',
  'leg_mix',
  'excluded_market_families',
  'stat_categories',
  'participants',
  'excluded_participants',
  'teams',
  'excluded_teams',
  'main_lines_only',
  'min_line',
  'max_line',
  'outcome_roles',
  'player_selection',
  'market_periods',
  'periods',
  'market_filters',
]

// Filter fields this file rebuilds; any other stored field is carried through
// untouched, so a field a newer server adds survives an edit and a save.
const MODELLED_FILTER_KEYS = new Set(KNOWN_FILTER_KEYS)

function otherFilterFields(filters) {
  return Object.fromEntries(Object.entries(filters || {}).filter(([key]) => !MODELLED_FILTER_KEYS.has(key)))
}

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const lowerKey = (value) => String(value ?? '').trim().toLocaleLowerCase()

// ---------- Per-market filters (#816) ----------

/** The sides a leg can sit on (the server's parlay.CanonicalOutcomeRoles), in its order. */
export const OUTCOME_ROLE_VOCABULARY = Object.freeze(['over', 'under', 'yes', 'no', 'home', 'draw', 'away'])

// Sides in the vocabulary's order, any other after them as given: the order
// the server saves a market's sides in (every filter profile lists its
// sides in this order), so an entry reads the same before and after a save.
const ROLE_RANK = new Map(OUTCOME_ROLE_VOCABULARY.map((role, index) => [role, index]))
const bySideOrder = (left, right) => (ROLE_RANK.get(left) ?? ROLE_RANK.size) - (ROLE_RANK.get(right) ?? ROLE_RANK.size)

/**
 * The fields one `filters.market_filters` entry can set
 * (models.PrepackMarketFilter); the server's strict decoding refuses any other.
 */
export const MARKET_FILTER_FIELDS = Object.freeze([
  'min_leg_odds',
  'max_leg_odds',
  'outcome_roles',
  'lines',
  'min_line',
  'max_line',
  'main_lines_only',
  'participants',
  'excluded_participants',
  'players',
  'excluded_players',
])

// The server keeps at most this many entries, like `market_periods`.
const MAX_MARKET_FILTERS = 50

const finiteOrNull = (value) => {
  if (value == null || value === '' || typeof value === 'boolean') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}
const trimmedStrings = (values) => (Array.isArray(values) ? values.map((value) => String(value ?? '').trim()).filter(Boolean) : [])

/**
 * A market's own line bound as it saves: a finite number, or an explicit
 * null (no bound, U6); undefined when the entry leaves the bound out (or
 * holds something else), so the pair inherits.
 */
const ownLineBound = (entry, field) => {
  if (!Object.hasOwn(entry, field)) return undefined
  if (entry[field] === null) return null
  const number = finiteOrNull(entry[field])
  return number == null ? undefined : number
}

/** Whether an entry's line pair is its own: either bound present, as a number or null. */
const lineBoundsSet = (entry) => ownLineBound(entry, 'min_line') !== undefined || ownLineBound(entry, 'max_line') !== undefined

// ---------- Team and player filters (issue 1015) ----------

// The server keeps at most this many teams or players per list, each name
// at most this many characters.
export const MAX_ENTITY_REFS = 50
const MAX_ENTITY_NAME = 100

/**
 * The key two team or player names are compared by, as the server's
 * ParticipantNameKey writes it: lowercase, accents dropped ("Atlético" is
 * "atletico"), spaces collapsed.
 */
export const foldName = (name) => String(name ?? '')
  .normalize('NFD')
  .replace(COMBINING_MARK_PATTERN, '')
  .toLocaleLowerCase()
  .replace(WHITESPACE_RUN_PATTERN, ' ')
  .trim()

/** The key two refs are the same by: the Kambi id, else the folded name. */
export const refKey = (ref) => {
  const id = String(ref?.id ?? '').trim()
  return id ? `id:${id}` : `name:${foldName(ref?.name)}`
}

const cleanText = (value) => String(value ?? '').trim().replace(WHITESPACE_RUN_PATTERN, ' ')

function cleanRefs(values, shape) {
  if (!Array.isArray(values)) return []
  const seen = new Set()
  const refs = []
  for (const value of values) {
    if (!isPlainObject(value)) continue
    const ref = shape(value)
    if (!ref.name) continue
    const key = refKey(ref)
    if (seen.has(key)) continue
    seen.add(key)
    refs.push(ref)
  }
  return refs
}

/**
 * A team list as it saves (`models.PrepackTeamRef`): trimmed, each team
 * once (by id, else folded name), nameless entries dropped, only the fields
 * the server knows.
 */
export const cleanTeamRefs = (values) => cleanRefs(values, (value) => {
  const id = cleanText(value.id)
  const league = cleanText(value.league)
  return { ...(id ? { id } : {}), name: cleanText(value.name), ...(league ? { league } : {}) }
})

/** A player list as it saves (`models.PrepackPlayerRef`), as cleanTeamRefs. */
export const cleanPlayerRefs = (values) => cleanRefs(values, (value) => {
  const optional = Object.fromEntries(['id', 'team_id', 'team', 'league']
    .map((field) => [field, cleanText(value[field])])
    .filter(([, text]) => text))
  const { id, ...rest } = optional
  return { ...(id ? { id } : {}), name: cleanText(value.name), ...rest }
})

/** The schedule's team lists as they save: only those that name a team. */
function teamFilterFields(filters) {
  const teams = cleanTeamRefs(filters?.teams)
  const excluded = cleanTeamRefs(filters?.excluded_teams)
  return {
    ...(teams.length > 0 ? { teams } : {}),
    ...(excluded.length > 0 ? { excluded_teams: excluded } : {}),
  }
}

/**
 * Whether two refs name the same team or player, as the server's
 * sameEntity reads them: by Kambi id when both carry one, else by folded
 * name, so a club picked from a roster and the same club found through
 * Search without an id are one.
 */
export function sameRef(left, right) {
  const leftId = cleanText(left?.id)
  const rightId = cleanText(right?.id)
  if (leftId && rightId) return leftId === rightId
  const key = foldName(left?.name)
  return Boolean(key) && key === foldName(right?.name)
}

/** The first ref in both lists (named as the kept list names it), or null. */
export function refConflict(included, excluded) {
  return (included || []).find((ref) => (excluded || []).some((other) => sameRef(ref, other))) || null
}

/**
 * Why a team's or player's league is outside the schedule's scope ("La
 * Liga, which this schedule no longer includes"), or null, as the server's
 * entityScopeProblem reads it: with leagues, the ref's league must be one
 * of them, except that under a parent league (ATP, PGA), whose tournaments
 * only Kambi's tree names, a league that is not a builder league may be one
 * of its tournaments; with only sports, a known league's sport must be one
 * of them. A ref without a league is never out of scope.
 */
export function entityScopeProblem(ref, definition) {
  const league = cleanText(ref?.league)
  if (!league) return null
  const leagues = (Array.isArray(definition?.leagues) ? definition.leagues : []).map((value) => leagueKey(value))
  const label = formatLeagueLabel(league)
  if (leagues.length > 0) {
    if (leagues.includes(leagueKey(league))) return null
    if (!leagueSportId(league) && leagues.some((id) => Boolean(leagueGroups[id]?.parent))) return null
    return `${label}, which this schedule no longer includes`
  }
  const sports = (Array.isArray(definition?.sports) ? definition.sports : []).map(canonicalSport)
  const sport = leagueSportId(league)
  if (sports.length === 0 || !sport) return null
  return sports.includes(canonicalSport(sport)) ? null : `${label}, whose sport this schedule no longer includes`
}

/** Why a chosen team no longer fits the schedule (a chip's note and the save's refusal), or null. */
export function teamScopeProblem(ref, definition) {
  const problem = entityScopeProblem(ref, definition)
  return problem ? `${ref.name} is in ${problem}. Remove the team or add it back.` : null
}

/**
 * Why a chosen player no longer fits the schedule, or null: the player's
 * league left the scope, the player's club is one the schedule never uses,
 * or the schedule keeps only some teams and not the player's club.
 */
export function playerScopeProblem(ref, definition) {
  const problem = entityScopeProblem(ref, definition)
  if (problem) return `${ref.name} is in ${problem}. Remove the player or add it back.`
  // Only a player the roster gave a club id is checked: a player found
  // through Search names the game's teams, not a club. A chosen team is the
  // club by id, or by name when the team has no id (found through Search),
  // as the server reads it.
  const teamId = cleanText(ref?.team_id)
  if (!teamId) return null
  const teamName = foldName(ref?.team)
  const isClub = (team) => (team.id ? team.id === teamId : Boolean(teamName) && foldName(team.name) === teamName)
  const filters = definition?.filters || {}
  const excluded = cleanTeamRefs(filters.excluded_teams).find(isClub)
  if (excluded) return `${ref.name} plays for ${excluded.name}, which this schedule never uses. Remove the player or the team.`
  const kept = cleanTeamRefs(filters.teams)
  if (kept.length > 0 && !kept.some(isClub)) {
    return `${ref.name} plays for ${cleanText(ref.team) || 'a team'}, which is not among this schedule's teams. Remove the player or add the team.`
  }
  return null
}

/**
 * The schedule's team problems, keyed as the server's fields (`teams`,
 * `excluded_teams`): a team in both lists, then a team out of scope, then
 * the lists' limits.
 */
function teamFilterProblems(filters, definition) {
  const teams = cleanTeamRefs(filters?.teams)
  const excluded = cleanTeamRefs(filters?.excluded_teams)
  const problems = {}
  const conflict = refConflict(teams, excluded)
  if (conflict) problems.teams = `${conflict.name} is in both Only these teams and Never these teams`
  for (const [field, refs] of [['teams', teams], ['excluded_teams', excluded]]) {
    if (problems[field]) continue
    if (refs.length > MAX_ENTITY_REFS) {
      problems[field] = `Choose at most ${MAX_ENTITY_REFS} teams`
      continue
    }
    const tooLong = refs.find((ref) => ref.name.length > MAX_ENTITY_NAME)
    if (tooLong) {
      problems[field] = `${tooLong.name.slice(0, 40)}… is longer than ${MAX_ENTITY_NAME} characters`
      continue
    }
    const outside = refs.map((ref) => teamScopeProblem(ref, definition)).find(Boolean)
    if (outside) problems[field] = outside
  }
  return problems
}

/**
 * One `market_filters` entry as it saves, or null when it sets nothing: only
 * the fields it sets (a field left out inherits the shared one), odds as
 * whole numbers (an explicit 0 stays: no limit for this market, over the
 * schedule's per-leg limits, as the server keeps it), a line bound as a
 * number or an explicit null (null stays: no bound, so `{ min_line: null,
 * max_line: null }` frees the market from the shared pair; the server's
 * NullableFloat), sides lowercase and unique in the server's order (over,
 * under, yes, no, home, draw, away, as it saves them), rungs as sorted
 * unique numbers, and no empty list (the server reads an empty list as
 * "inherit" and drops it).
 */
export function cleanMarketFilterEntry(entry) {
  if (!isPlainObject(entry)) return null
  const odds = (value) => {
    const number = finiteOrNull(value)
    return number == null ? null : Math.trunc(number)
  }
  const minLegOdds = odds(entry.min_leg_odds)
  const maxLegOdds = odds(entry.max_leg_odds)
  const roles = [...new Set(trimmedStrings(entry.outcome_roles).map((role) => role.toLocaleLowerCase()))].sort(bySideOrder)
  const lines = Array.isArray(entry.lines)
    ? [...new Set(entry.lines.map(finiteOrNull).filter((line) => line != null))].sort((a, b) => a - b)
    : []
  const minLine = ownLineBound(entry, 'min_line')
  const maxLine = ownLineBound(entry, 'max_line')
  const participants = trimmedStrings(entry.participants)
  const excluded = trimmedStrings(entry.excluded_participants)
  const players = cleanPlayerRefs(entry.players)
  const excludedPlayers = cleanPlayerRefs(entry.excluded_players)
  const cleaned = {
    ...(minLegOdds != null ? { min_leg_odds: minLegOdds } : {}),
    ...(maxLegOdds != null ? { max_leg_odds: maxLegOdds } : {}),
    ...(roles.length > 0 ? { outcome_roles: roles } : {}),
    ...(lines.length > 0 ? { lines } : {}),
    ...(minLine !== undefined ? { min_line: minLine } : {}),
    ...(maxLine !== undefined ? { max_line: maxLine } : {}),
    ...(typeof entry.main_lines_only === 'boolean' ? { main_lines_only: entry.main_lines_only } : {}),
    ...(participants.length > 0 ? { participants } : {}),
    ...(excluded.length > 0 ? { excluded_participants: excluded } : {}),
    ...(players.length > 0 ? { players } : {}),
    ...(excludedPlayers.length > 0 ? { excluded_players: excludedPlayers } : {}),
  }
  return Object.keys(cleaned).length > 0 ? cleaned : null
}

/**
 * `filters.market_filters` (#816): `{ market key: entry }`, a copy with
 * lowercase keys, each entry as `cleanMarketFilterEntry` writes it, only the
 * `keys` given (the selected markets; every key when omitted), and no entry
 * that sets nothing; undefined when none is left. The server does the same
 * and refuses a field the market's filter profile does not support.
 */
export function cleanMarketFilters(value, keys = null) {
  if (!isPlainObject(value)) return undefined
  const allowed = keys ? new Set(keys.map(lowerKey)) : null
  const result = {}
  for (const [rawKey, entry] of Object.entries(value)) {
    const key = lowerKey(rawKey)
    if (!key || (allowed && !allowed.has(key))) continue
    const cleaned = cleanMarketFilterEntry(entry)
    if (cleaned) result[key] = cleaned
  }
  return Object.keys(result).length > 0 ? result : undefined
}

/** The catalog's filter profile per market key (`filter_options.market_keys[].filter_profile`); empty from an older server. */
export function filterProfilesByKey(serverFilterOptions) {
  const profiles = new Map()
  for (const option of Array.isArray(serverFilterOptions?.market_keys) ? serverFilterOptions.market_keys : []) {
    if (isPlainObject(option?.filter_profile)) profiles.set(lowerKey(option.value), option.filter_profile)
  }
  return profiles
}

/**
 * Whether the server serves per-market filters: its catalog's market keys
 * carry a filter profile. An older server refuses `market_filters`, so the
 * builder writes it only then, as it writes `market_periods` only with the
 * period vocabulary.
 */
export const supportsMarketFilters = (serverFilterOptions) => filterProfilesByKey(serverFilterOptions).size > 0

const isAmericanOdds = (value) => value === 0 || Math.abs(value) >= 100

// The selection filters' limits the server holds every list and line to
// (parlay.ValidateGenerationSelectionFilters, ValidateParticipantLists).
const MAX_FILTER_LIST_ITEMS = 50
const MAX_FILTER_ITEM_BYTES = 128
const MAX_SELECTION_LINES = 50
const MAX_SELECTION_LINE = 10000
// Decimal odds the server keeps to seven whole digits.
const MAX_DECIMAL_ODDS = 1e7
const utf8 = new TextEncoder()
// unicode.IsControl's range: C0, DEL and C1.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/

/**
 * A name as the server compares participants (parlay.normalizedContractToken):
 * lowercase, ASCII letters and digits only, so "GB Packers", "gb packers" and
 * "G.B. Packers" are one name. "" for a name with none.
 */
export const contractToken = (value) => String(value ?? '').trim().toLowerCase().replace(NON_ALPHANUMERIC_PATTERN, '')

/**
 * The first excluded participant the server reads as also included, or
 * undefined: the same trimmed value, or the same non-empty contract token
 * (parlay.participantListsContradiction).
 */
function contradictoryParticipant(included, excluded) {
  const exact = new Set(included)
  const tokens = new Set(included.map(contractToken).filter(Boolean))
  return excluded.find((value) => exact.has(value) || (contractToken(value) !== '' && tokens.has(contractToken(value))))
}

// What the server says of one participant list over its limits
// (validateFilterStringList: at most 50 items, each at most 128 bytes and
// without control characters), or null. `name` is the list's path.
function participantListProblem(values, name) {
  if (values.length > MAX_FILTER_LIST_ITEMS) return `selection filters ${name} exceeds maximum length of ${MAX_FILTER_LIST_ITEMS}`
  if (values.some((value) => utf8.encode(value).length > MAX_FILTER_ITEM_BYTES)) return `selection filters ${name} item exceeds maximum length of ${MAX_FILTER_ITEM_BYTES}`
  if (values.some((value) => CONTROL_CHARACTERS.test(value))) return `selection filters ${name} item cannot contain control characters`
  return null
}

const outOfLineRange = (line) => line < -MAX_SELECTION_LINE || line > MAX_SELECTION_LINE

/**
 * The first problem with one `market_filters` entry, in the server's order
 * and words (prepacks.cleanMarketFilter), or null. `option` is the key's
 * catalog market (its label and `filter_profile`; profile checks are
 * skipped without one); `definition` gives the per-leg limits the entry's
 * odds replace one bound at a time. The selection filters' limits follow
 * (rungs, line bounds and odds in range, at most 50 rungs), then the
 * participant lists' limits and a participant both kept and left out,
 * compared as the server compares names (`contractToken`).
 */
function marketFilterEntryProblem(key, entry, option, definition) {
  const field = `market_filters.${key}`
  const label = String(option?.label || '').trim() || key
  const profile = isPlainObject(option?.filter_profile) ? option.filter_profile : null
  const sides = Array.isArray(profile?.sides) ? profile.sides.map(lowerKey) : []

  if (entry.outcome_roles != null && (!Array.isArray(entry.outcome_roles) || !entry.outcome_roles.every((role) => typeof role === 'string'))) {
    return 'Outcome sides must be a list of side names'
  }
  for (const role of trimmedStrings(entry.outcome_roles).map((value) => value.toLocaleLowerCase())) {
    if (!OUTCOME_ROLE_VOCABULARY.includes(role)) return `unsupported outcome role "${role}"; use one of ${OUTCOME_ROLE_VOCABULARY.join(', ')}`
    if (profile && !sides.includes(role)) return `${label} has no ${role} side`
  }
  const lines = Array.isArray(entry.lines) ? entry.lines.map(finiteOrNull).filter((line) => line != null) : []
  if (lines.length > 0 && profile && !profile.milestones) return `${label} has no milestones`
  const minLine = finiteOrNull(entry.min_line)
  const maxLine = finiteOrNull(entry.max_line)
  // A pair present at all, null halves included, is the market's own bounds, so it needs a line.
  if (lineBoundsSet(entry) && profile && !profile.line_range) return `${label} has no line`
  if (minLine != null && maxLine != null && minLine > maxLine) return `${field}: min_line cannot exceed max_line`
  if (typeof entry.main_lines_only === 'boolean' && profile && !profile.main_line) return `${label} has no main line`
  const participants = trimmedStrings(entry.participants)
  const excluded = trimmedStrings(entry.excluded_participants)
  // Legacy participant lists are kept on any market (issue 1015); players
  // need a player market, and no player in both lists.
  const players = cleanPlayerRefs(entry.players)
  const excludedPlayers = cleanPlayerRefs(entry.excluded_players)
  if ((players.length > 0 || excludedPlayers.length > 0) && profile && lowerKey(profile.participants) !== 'players') {
    return `${label} names no players to keep or leave out`
  }
  if (players.length > MAX_ENTITY_REFS || excludedPlayers.length > MAX_ENTITY_REFS) return `Choose at most ${MAX_ENTITY_REFS} players for ${label}`
  const playerConflict = refConflict(players, excludedPlayers)
  if (playerConflict) return `${playerConflict.name} is in both Only these players and Never these players for ${label}`
  const outsidePlayer = [...players, ...excludedPlayers].map((ref) => playerScopeProblem(ref, definition)).find(Boolean)
  if (outsidePlayer) return outsidePlayer

  const own = (value) => {
    const number = finiteOrNull(value)
    return number == null ? null : Math.trunc(number)
  }
  const minLegOdds = own(entry.min_leg_odds)
  const maxLegOdds = own(entry.max_leg_odds)
  if (minLegOdds != null && !isAmericanOdds(minLegOdds)) return `invalid ${field}.min_leg_odds: enter valid American odds`
  if (maxLegOdds != null && !isAmericanOdds(maxLegOdds)) return `invalid ${field}.max_leg_odds: enter valid American odds`
  const effectiveMin = minLegOdds ?? (Number(definition?.min_leg_odds) || 0)
  const effectiveMax = maxLegOdds ?? (Number(definition?.max_leg_odds) || 0)
  if (effectiveMin && effectiveMax && americanToDecimal(effectiveMin) > americanToDecimal(effectiveMax)) {
    if (minLegOdds != null && maxLegOdds != null) return `${field}: min_leg_odds cannot exceed max_leg_odds`
    if (minLegOdds != null) return `${field}: min_leg_odds ${formatSignedOdds(effectiveMin)} is above the schedule's max_leg_odds ${formatSignedOdds(effectiveMax)}`
    if (maxLegOdds != null) return `${field}: max_leg_odds ${formatSignedOdds(effectiveMax)} is below the schedule's min_leg_odds ${formatSignedOdds(effectiveMin)}`
  }

  // The selection filters' own limits, in their order: odds, line bounds,
  // then the rungs (as the save sends them: unique).
  const limit = (problem) => `Invalid ${field}: selection filters ${problem}`
  if (minLegOdds && americanToDecimal(minLegOdds) >= MAX_DECIMAL_ODDS) return limit('min_leg_decimal_odds must be a bounded decimal greater than 1')
  if (maxLegOdds && americanToDecimal(maxLegOdds) >= MAX_DECIMAL_ODDS) return limit('max_leg_decimal_odds must be a bounded decimal greater than 1')
  if (minLine != null && outOfLineRange(minLine)) return limit('min_line out of bounded range')
  if (maxLine != null && outOfLineRange(maxLine)) return limit('max_line out of bounded range')
  const rungs = [...new Set(lines)]
  if (rungs.length > MAX_SELECTION_LINES) return limit(`lines exceeds maximum length of ${MAX_SELECTION_LINES}`)
  const outside = rungs.find(outOfLineRange)
  if (outside != null) return limit(`lines item "${outside}" must be a decimal between -${MAX_SELECTION_LINE} and ${MAX_SELECTION_LINE}`)

  const listProblem = participantListProblem(participants, `${field}.participants`)
    || participantListProblem(excluded, `${field}.excluded_participants`)
  if (listProblem) return `Invalid market filters: ${listProblem}`
  const contradiction = contradictoryParticipant(participants, excluded)
  if (contradiction) {
    return `Invalid market filters: selection filters contain contradictory participant "${contradiction}" in both included and excluded lists of ${field}`
  }
  return null
}

/**
 * Problems with `filters.market_filters`, keyed `market_filters.<key>` (one
 * per market, the first the server would name) or `market_filters` for the
 * map itself. An entry for a market that is no longer selected is not one:
 * it is dropped on save (`cleanRecipeForSave`), as the server would refuse it.
 */
export function marketFilterProblems(filters, definition = {}, serverFilterOptions = null) {
  const value = filters?.market_filters
  if (value == null) return {}
  if (!isPlainObject(value) || !Object.values(value).every((entry) => entry == null || isPlainObject(entry))) {
    return { market_filters: 'Market filters must map each market to its filters' }
  }
  const selected = new Set(trimmedStrings(filters.market_keys).map((key) => key.toLocaleLowerCase()))
  const keys = Object.keys(value).filter((rawKey) => selected.has(lowerKey(rawKey)) && value[rawKey] != null)
  if (keys.length > MAX_MARKET_FILTERS) return { market_filters: `market_filters may name at most ${MAX_MARKET_FILTERS} markets` }
  const catalog = new Map((Array.isArray(serverFilterOptions?.market_keys) ? serverFilterOptions.market_keys : [])
    .map((option) => [lowerKey(option?.value), option]))
  const problems = {}
  for (const rawKey of keys.sort()) {
    const key = lowerKey(rawKey)
    const problem = marketFilterEntryProblem(key, value[rawKey], catalog.get(key), definition)
    if (problem && !problems[`market_filters.${key}`]) problems[`market_filters.${key}`] = problem
  }
  return problems
}

const MARKET_FILTER_STRING_LISTS = ['outcome_roles', 'participants', 'excluded_participants']

/** The message Advanced JSON gives a new per-market entry on a server without filter profiles. */
export const MARKET_FILTERS_UNSUPPORTED = 'This server does not support per-market filters'

/**
 * What Advanced JSON's `filters.market_filters` gets wrong, or null: the
 * types the server decodes (an object of objects; whole-number odds; lists of
 * strings; rungs as finite numbers; line bounds as finite numbers or null,
 * null being no bound; a boolean main-line switch), no field it does not
 * know, and each market named once (in the server's words). `heldKeys` are
 * the keys the draft already has an entry for: such an entry may name a
 * market that is not selected (a removed market's filters, which the save
 * drops), and is the only kind a server without filter profiles takes
 * (`supported` false), since only that server sent it; any other entry must
 * name a selected market, and needs a server that serves profiles.
 */
function marketFiltersJsonProblem(value, marketKeys, { heldKeys = new Set(), supported = true } = {}) {
  if (!isPlainObject(value) || !Object.values(value).every(isPlainObject)) {
    return 'filters.market_filters must map each market key to an object of filters'
  }
  const selected = new Set((Array.isArray(marketKeys) ? marketKeys : []).map(lowerKey))
  const seen = new Set()
  for (const [rawKey, entry] of Object.entries(value)) {
    const path = `filters.market_filters.${rawKey}`
    const unknown = Object.keys(entry).filter((field) => !MARKET_FILTER_FIELDS.includes(field))
    if (unknown.length > 0) return `${path} has unknown field(s): ${unknown.join(', ')}. Unsupported fields are not permitted.`
    for (const field of ['min_leg_odds', 'max_leg_odds']) {
      if (entry[field] != null && !Number.isInteger(entry[field])) return `${path}.${field} must be an integer`
    }
    for (const field of MARKET_FILTER_STRING_LISTS) {
      if (entry[field] != null && (!Array.isArray(entry[field]) || !entry[field].every((item) => typeof item === 'string'))) {
        return `${path}.${field} must be an array of strings`
      }
    }
    for (const field of ['players', 'excluded_players']) {
      if (entry[field] != null && (!Array.isArray(entry[field]) || !entry[field].every((ref) => isPlainObject(ref) && typeof ref.name === 'string'))) {
        return `${path}.${field} must be an array of players, each with a name`
      }
    }
    if (entry.lines != null && (!Array.isArray(entry.lines) || !entry.lines.every((line) => typeof line === 'number' && Number.isFinite(line)))) {
      return `${path}.lines must be an array of numbers`
    }
    for (const field of ['min_line', 'max_line']) {
      if (entry[field] != null && (typeof entry[field] !== 'number' || !Number.isFinite(entry[field]))) return `${path}.${field} must be a finite number or null`
    }
    if (entry.main_lines_only != null && typeof entry.main_lines_only !== 'boolean') return `${path}.main_lines_only must be a boolean`
    const key = lowerKey(rawKey)
    const held = heldKeys.has(key)
    if (!held && !supported) return `${MARKET_FILTERS_UNSUPPORTED}: remove filters.market_filters.${rawKey}`
    if (!held && !selected.has(key)) return `market_filters names "${rawKey}", which is not among the selected markets`
    if (seen.has(key)) return `market_filters names "${key}" more than once`
    seen.add(key)
  }
  return null
}

/**
 * `filters.market_filters` with only the selected markets' entries
 * (undefined when none is left): what Advanced JSON shows and applies. The
 * draft keeps a removed market's entry so Undo brings it back, but the save
 * drops it, so the JSON leaves it out.
 */
function selectedMarketFilters(filters) {
  if (!isPlainObject(filters?.market_filters)) return filters?.market_filters
  const selected = new Set(trimmedStrings(filters.market_keys).map((key) => key.toLocaleLowerCase()))
  const kept = Object.entries(filters.market_filters).filter(([rawKey]) => selected.has(lowerKey(rawKey)))
  return kept.length > 0 ? Object.fromEntries(kept) : undefined
}

// `filters.market_periods` (#609): `{ market key: [period ids] }`, a copy
// with lowercase keys and ids, unique and non-blank; undefined when none.
function cleanMarketPeriods(value, keys = null) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const allowed = keys ? new Set(keys) : null
  const result = {}
  for (const [rawKey, ids] of Object.entries(value)) {
    const key = String(rawKey).trim().toLocaleLowerCase()
    if (!key || (allowed && !allowed.has(key)) || !Array.isArray(ids)) continue
    const list = [...new Set(ids.map((id) => String(id ?? '').trim().toLocaleLowerCase()).filter(Boolean))]
    if (list.length > 0) result[key] = [...new Set([...(result[key] || []), ...list])]
  }
  return Object.keys(result).length > 0 ? result : undefined
}

// `filters.periods`: kept as stored (a copy), absent when not a list.
function copyPeriods(value) {
  return Array.isArray(value) ? value.map((id) => String(id ?? '').trim().toLocaleLowerCase()).filter(Boolean) : undefined
}

// The optional per-market fields, only when set, so an unset one never appears as a key.
function periodFields(marketPeriods, periods, marketFilters) {
  return {
    ...(marketPeriods ? { market_periods: marketPeriods } : {}),
    ...(periods ? { periods } : {}),
    ...(marketFilters ? { market_filters: marketFilters } : {}),
  }
}

// "HH:MM" on a 24-hour clock; the strings compare lexically in this form.
export const START_TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/
/**
 * Converts an American odds bound to decimal for ordering.
 * e.g. -200 -> 1.5, -110 -> 1.909, +100 -> 2.0, +150 -> 2.5
 * 0 is unconstrained, and so is anything inside ±100 (returns 0).
 */
export function americanToDecimal(value) {
  return Math.abs(Number(value)) >= 100 ? (decimalFromAmerican(value) ?? 0) : 0
}

/**
 * Formats an American odds bound for display, adding a '+' for positive odds.
 */
export function formatAmericanOdds(value) {
  return formatSignedOdds(value, { empty: 'Unconstrained' })
}

/**
 * "2" for one size, "2–6" for a contiguous run, "2, 4, 5" otherwise.
 */
export function formatCompactLegs(legs = []) {
  if (!legs || legs.length === 0) return 'Any'
  const sorted = [...new Set(legs)].sort((a, b) => a - b)
  if (sorted.length === 1) return String(sorted[0])
  const contiguous = sorted.every((count, index) => index === 0 || count === sorted[index - 1] + 1)
  return contiguous ? `${sorted[0]}\u2013${sorted[sorted.length - 1]}` : sorted.join(', ')
}

// A line bound as a number, or null when it is empty or not a number.
function parseLine(value) {
  if (value == null || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

// bet_type is optional: the server infers it from the strategy when absent.
function normalizeBetType(value) {
  return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : ''
}

/**
 * Deep clones definition object.
 */
export function cloneDefinition(def = {}) {
  const filters = def.filters || {}
  const betType = normalizeBetType(def.bet_type)
  const mix = cleanLegMix(filters.leg_mix)
  return {
    version: def.version != null && Number.isInteger(Number(def.version)) ? Number(def.version) : 1,
    strategy: def.strategy || '',
    ...(betType ? { bet_type: betType } : {}),
    sports: Array.isArray(def.sports) ? [...def.sports] : [],
    leagues: Array.isArray(def.leagues) ? [...def.leagues] : [],
    leg_counts: Array.isArray(def.leg_counts)
      ? [...new Set(def.leg_counts)].sort((a, b) => a - b)
      : [],
    min_odds: Number(def.min_odds) || 0,
    max_odds: Number(def.max_odds) || 0,
    min_leg_odds: Number(def.min_leg_odds) || 0,
    max_leg_odds: Number(def.max_leg_odds) || 0,
    event_lookahead_hours: def.event_lookahead_hours != null && Number.isFinite(Number(def.event_lookahead_hours)) && Number(def.event_lookahead_hours) >= 0 ? Number(def.event_lookahead_hours) : 48,
    start_time_after: def.start_time_after || '',
    start_time_before: def.start_time_before || '',
    filters: {
      ...otherFilterFields(filters),
      market_families: Array.isArray(filters.market_families) ? [...filters.market_families] : [],
      market_keys: Array.isArray(filters.market_keys) ? [...filters.market_keys] : [],
      ...(mix.length ? { leg_mix: mix } : {}),
      excluded_market_families: Array.isArray(filters.excluded_market_families) ? [...filters.excluded_market_families] : [],
      stat_categories: Array.isArray(filters.stat_categories) ? [...filters.stat_categories] : [],
      participants: Array.isArray(filters.participants) ? [...filters.participants] : [],
      excluded_participants: Array.isArray(filters.excluded_participants) ? [...filters.excluded_participants] : [],
      ...teamFilterFields(filters),
      main_lines_only: Boolean(filters.main_lines_only),
      min_line: parseLine(filters.min_line),
      max_line: parseLine(filters.max_line),
      outcome_roles: Array.isArray(filters.outcome_roles) ? [...filters.outcome_roles] : [],
      player_selection: typeof filters.player_selection === 'string' ? filters.player_selection : '',
      // Every stored entry, cleaned: a key that is no longer selected keeps
      // its entry until a save drops it (so Undo of a removal brings it back).
      ...periodFields(cleanMarketPeriods(filters.market_periods), copyPeriods(filters.periods), cleanMarketFilters(filters.market_filters)),
    },
  }
}

/**
 * The recipe a schedule made from a pack starts from: the draft's copied
 * rules with the organization's odds limits (Configuration's minimum and
 * maximum odds, American, 0 for none), never the pack's price. `config` must
 * have loaded.
 */
export function recipeFromPackDraft(recipe, config) {
  return { ...recipe, definition: { ...recipe.definition, min_odds: Number(config?.min_odds) || 0, max_odds: Number(config?.max_odds) || 0 } }
}

/**
 * Creates a blank sensible recipe. The leg counts start on All sizes, every
 * count the pack type offers.
 */
export function newRecipe(serverStrategies = []) {
  const defaultStrategy = serverStrategies?.[0]?.id || ''
  const descriptor = getStrategyDescriptor(defaultStrategy, serverStrategies)
  return {
    id: null,
    organization_id: null,
    name: 'New schedule',
    description: '',
    revision: 0,
    usage_count: 0,
    definition: {
      version: 1,
      strategy: defaultStrategy,
      sports: Array.isArray(descriptor?.sports) && descriptor.sports.length ? [...descriptor.sports] : [],
      leagues: [],
      leg_counts: defaultLegCounts(descriptor),
      min_odds: 100,
      max_odds: 1500,
      min_leg_odds: 0,
      max_leg_odds: 0,
      event_lookahead_hours: 48,
      start_time_after: '',
      start_time_before: '',
      filters: {
        market_families: [],
        market_keys: [],
        excluded_market_families: [],
        stat_categories: [],
        participants: [],
        excluded_participants: [],
        main_lines_only: false,
        min_line: null,
        max_line: null,
        outcome_roles: [],
        player_selection: '',
      },
    },
  }
}

/**
 * Duplicates an existing recipe for creating a variant. The server assigns a new
 * UUID on POST. A copy's source is the schedule, so the pack it was created from
 * (`source_prepack_*`) stays behind.
 */
export function duplicateRecipe(source) {
  if (!source) return newRecipe()
  return {
    id: null,
    organization_id: null,
    name: `${source.name || 'Schedule'} (Copy)`,
    description: source.description || '',
    revision: 0,
    usage_count: 0,
    definition: cloneDefinition(source.definition),
  }
}

/**
 * Normalizes a recipe record received from server or draft.
 */
export function normalizeRecipe(raw) {
  if (!raw) return newRecipe()
  return {
    id: raw.id ?? null,
    organization_id: raw.organization_id ?? null,
    name: raw.name || '',
    description: raw.description || '',
    revision: Number(raw.revision) || 0,
    usage_count: Number(raw.usage_count) || 0,
    built_in: raw.built_in === true,
    master_set: raw.master_set ?? null,
    master_organization_id: raw.master_organization_id ?? null,
    master_edited: raw.master_edited === true,
    // Saved with Save draft: never runs until saved without it. The saves
    // send it themselves; cleanRecipeForSave leaves it out.
    is_draft: raw.is_draft === true,
    // The pack it was created from; read-only, the server stamps them.
    source_prepack_id: raw.source_prepack_id ?? null,
    source_prepack_title: raw.source_prepack_title || '',
    created_at: raw.created_at || null,
    updated_at: raw.updated_at || null,
    definition: cloneDefinition(raw.definition),
  }
}

/**
 * Resolves the strategy descriptor for a given strategy key from server descriptors.
 * Returns null if the strategy is not found in the server catalog.
 */
export function isValidStrategyDescriptor(descriptor) {
  if (!descriptor || typeof descriptor !== 'object') return false
  if (typeof descriptor.id !== 'string' || !descriptor.id.trim()) return false
  if (typeof descriptor.label !== 'string' || !descriptor.label.trim()) return false
  if (typeof descriptor.kind !== 'string' || !descriptor.kind.trim()) return false
  if (!Array.isArray(descriptor.sports)) return false
  if (!descriptor.sports.every((s) => typeof s === 'string' && s.trim())) return false
  if (!Array.isArray(descriptor.leg_counts) || descriptor.leg_counts.length === 0) return false
  if (!descriptor.leg_counts.every((n) => typeof n === 'number' && Number.isInteger(n) && n > 0)) return false
  return true
}

/**
 * Derives the list of selectable leg counts from the strategy descriptor
 * and any currently selected values.
 */
export function getSelectableLegCounts(descriptor, selectedLegCounts = []) {
  const descriptorCounts = Array.isArray(descriptor?.leg_counts) ? descriptor.leg_counts : []
  const selected = Array.isArray(selectedLegCounts) ? selectedLegCounts : []
  const combined = new Set([
    ...ALL_LEG_COUNTS,
    ...descriptorCounts.map(Number).filter((n) => Number.isInteger(n) && n > 0),
    ...selected.map(Number).filter((n) => Number.isInteger(n) && n > 0),
  ])
  return Array.from(combined).sort((a, b) => a - b)
}

/**
 * Resolves the strategy descriptor for a given strategy key from server descriptors.
 * Returns null if the strategy is not found in the server catalog or is malformed.
 */
export function getStrategyDescriptor(strategyId, serverStrategies = []) {
  if (!strategyId) return null
  const found = (serverStrategies || []).find((s) => s?.id === strategyId)
  if (!found || !isValidStrategyDescriptor(found)) return null
  return found
}

/**
 * Groups the pack-type select. General packs first, then the sports
 * algorithms by which team(s) a pack's legs come from. Every strategy lands
 * in exactly one group, so the select never lists one twice.
 */
export const STRATEGY_GROUPS = [
  { id: 'generic', label: 'General' },
  { id: 'same_team', label: 'Same Game: Same Team' },
  { id: 'opposing_teams', label: 'Same Game: Opposing Teams' },
  { id: 'either_team', label: 'Same Game: Either Team' },
]

export function strategyGroup(descriptor) {
  if (descriptor?.kind !== 'sports') return 'generic'
  const id = String(descriptor.id || '')
  if (id.startsWith('cross_team_')) return 'opposing_teams'
  if (id.startsWith('same_game_')) return 'either_team'
  return 'same_team'
}

/**
 * Returns allowed leg counts for a strategy based on its descriptor.
 * Derives bounds strictly from the server descriptor; does not invent constraints.
 */
export function getAllowedLegCounts(strategyId, descriptor) {
  if (Array.isArray(descriptor?.leg_counts) && descriptor.leg_counts.length > 0) {
    return [...descriptor.leg_counts].map(Number).filter((n) => Number.isInteger(n) && n > 0).sort((a, b) => a - b)
  }
  return []
}

/**
 * All sizes: the counts a schedule builds when the operator picks none in
 * particular, every count the pack type offers. Leg counts are the
 * schedule's own; the brand sets none.
 */
export function defaultLegCounts(descriptor) {
  return getAllowedLegCounts(descriptor?.id, descriptor)
}

/**
 * Whether `legCounts` holds every count in `all` (from defaultLegCounts), the
 * All sizes state. It is derived, never stored: All sizes saves the full list.
 * A pack type with one size has no such state.
 */
export function coversAllLegCounts(legCounts, all) {
  return all.length > 1 && all.every((count) => (legCounts || []).includes(count))
}

/**
 * The recipe with a new pack type, constrained the way the pack type requires:
 * leg counts it does not permit are dropped (falling back to All sizes), and
 * a pack type that names its sports keeps only those (falling back to its
 * first sport). Pure; the caller stores the result.
 */
export function applyStrategyChange(recipe, strategyId, serverStrategies = []) {
  const descriptor = getStrategyDescriptor(strategyId, serverStrategies)
  const allowedLegs = getAllowedLegCounts(strategyId, descriptor)
  const definition = recipe?.definition || {}
  let legCounts = (definition.leg_counts || []).filter((count) => allowedLegs.includes(count))
  if (!legCounts.length) legCounts = defaultLegCounts(descriptor)
  let sports = definition.sports || []
  if (descriptor?.sports?.length) {
    // Saved tokens are rewritten to the pack type's own values ('NFL' becomes
    // 'football'), so what is kept reads the way validation compares it.
    const bySport = new Map()
    for (const sport of descriptor.sports) {
      const key = canonicalSport(sport)
      if (!bySport.has(key)) bySport.set(key, sport)
    }
    sports = [...new Set(sports.map((sport) => bySport.get(canonicalSport(sport))).filter(Boolean))]
    if (!sports.length) sports = [descriptor.sports[0]]
  }
  // Re-derive bet_type from the new pack type so a stale value never mismatches.
  const { bet_type: _previousBetType, ...rest } = definition
  const betType = normalizeBetType(descriptor?.bet_type)
  let filters = definition.filters
  if (filters?.leg_mix && !supportsLegMix({ strategy: strategyId }, descriptor)) {
    const { leg_mix: _mix, ...restFilters } = filters
    filters = restFilters
  } else if (strategyId !== 'same_game_parlay' && filters?.leg_mix?.length) {
    filters = { ...filters, leg_mix: cleanLegMix(filters.leg_mix).map(({ same_player_as: _player, same_team_as: _team, ...row }) => row) }
  }
  return {
    ...recipe,
    definition: {
      ...rest,
      strategy: strategyId,
      ...(betType ? { bet_type: betType } : {}),
      leg_counts: legCounts,
      sports,
      ...(filters ? { filters } : {}),
    },
  }
}

/**
 * Cleans and sanitizes recipe for API submission (POST / PUT).
 */
export function cleanRecipeForSave(recipe, options = {}) {
  const isCreate = typeof options === 'boolean' ? options : Boolean(options?.isCreate)
  const def = recipe?.definition || {}
  const filters = def.filters || {}

  const cleanArr = (arr) => (Array.isArray(arr) ? arr.map((s) => String(s).trim()).filter(Boolean) : [])
  const marketKeys = cleanArr(filters.market_keys).map((s) => s.toLocaleLowerCase())

  const betType = normalizeBetType(def.bet_type)
  const mix = supportsLegMix(def) && !filters.market_families?.length ? cleanLegMix(filters.leg_mix) : []
  const cleanedDef = {
    version: 1,
    strategy: String(def.strategy || '').trim(),
    ...(betType ? { bet_type: betType } : {}),
    sports: cleanArr(def.sports),
    leagues: cleanArr(def.leagues),
    leg_counts: Array.isArray(def.leg_counts)
      ? [...new Set(def.leg_counts
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0))]
        .sort((a, b) => a - b)
      : [],
    min_odds: Number(def.min_odds) || 0,
    max_odds: Number(def.max_odds) || 0,
    min_leg_odds: Number(def.min_leg_odds) || 0,
    max_leg_odds: Number(def.max_leg_odds) || 0,
    event_lookahead_hours: Math.max(0, Number(def.event_lookahead_hours) || 0),
    start_time_after: typeof def.start_time_after === 'string' ? def.start_time_after.trim() : '',
    start_time_before: typeof def.start_time_before === 'string' ? def.start_time_before.trim() : '',
    filters: {
      ...otherFilterFields(filters),
      market_families: cleanArr(filters.market_families),
      market_keys: marketKeys,
      ...(mix.length ? { leg_mix: mix } : {}),
      excluded_market_families: cleanArr(filters.excluded_market_families),
      stat_categories: cleanArr(filters.stat_categories),
      participants: cleanArr(filters.participants),
      excluded_participants: cleanArr(filters.excluded_participants),
      ...teamFilterFields(filters),
      main_lines_only: Boolean(filters.main_lines_only),
      min_line: parseLine(filters.min_line),
      max_line: parseLine(filters.max_line),
      outcome_roles: cleanArr(filters.outcome_roles).map((s) => s.toLocaleLowerCase()),
      player_selection: typeof filters.player_selection === 'string' ? filters.player_selection.trim() : '',
      // Periods and per-market filters of the selected keys only; `periods`
      // as stored. The builder writes `market_filters` only for a server
      // that serves filter profiles (`supportsMarketFilters`), so it is only
      // here when the server sent it or understands it.
      ...periodFields(
        cleanMarketPeriods(filters.market_periods, marketKeys),
        copyPeriods(filters.periods),
        cleanMarketFilters(filters.market_filters, marketKeys),
      ),
    },
  }

  const payload = {
    name: String(recipe?.name || '').trim(),
    description: String(recipe?.description || '').trim(),
    definition: cleanedDef,
  }

  if (!isCreate && (recipe?.revision != null || options?.revision != null)) {
    payload.revision = Number(options?.revision ?? recipe?.revision) || 0
  }

  return payload
}

/**
 * What is wrong with `filters.market_periods`, or null: it must map keys to
 * lists of period ids, and each id must be one the catalog offers for its
 * key (checked only when the catalog lists periods).
 */
function marketPeriodsProblem(value, serverFilterOptions) {
  if (value == null) return null
  const valid = typeof value === 'object' && !Array.isArray(value)
    && Object.values(value).every((ids) => Array.isArray(ids) && ids.every((id) => typeof id === 'string' && id.trim()))
  if (!valid) return 'Market periods must list period names for each market'
  const vocabulary = Array.isArray(serverFilterOptions?.periods) ? serverFilterOptions.periods : []
  if (vocabulary.length === 0) return null
  const known = new Set(vocabulary.map((period) => String(period?.value ?? '').trim().toLocaleLowerCase()))
  const byKey = new Map((Array.isArray(serverFilterOptions?.market_keys) ? serverFilterOptions.market_keys : [])
    .map((option) => [String(option?.value ?? '').trim().toLocaleLowerCase(), Array.isArray(option?.periods) ? option.periods.map((id) => String(id).toLocaleLowerCase()) : null]))
  for (const [key, ids] of Object.entries(value)) {
    const offered = byKey.get(key.trim().toLocaleLowerCase())
    // "any", alone, lets the key through in every period.
    if (ids.length === 1 && ids[0].trim().toLocaleLowerCase() === 'any') continue
    const unknown = ids.some((id) => {
      const period = id.trim().toLocaleLowerCase()
      return !known.has(period) || (offered && offered.length > 0 && !offered.includes(period))
    })
    if (unknown) return 'A selected market period is not offered by the catalog'
  }
  return null
}

/**
 * Validates a recipe against schema and strategy constraints.
 */
export function validateRecipe(recipe, serverStrategies = [], serverFilterOptions = null) {
  const errors = {}

  const name = String(recipe?.name || '').trim()
  if (!name) {
    errors.name = 'Name is required'
  }

  const def = recipe?.definition || {}
  if (def && typeof def === 'object' && !Array.isArray(def)) {
    const unknownDefKeys = Object.keys(def).filter((k) => !KNOWN_DEFINITION_KEYS.includes(k))
    if (unknownDefKeys.length > 0) {
      errors.definition = `Unknown definition field(s): ${unknownDefKeys.join(', ')}`
    }
    // Filter fields Studio does not model are a newer server's and are carried
    // through untouched (`otherFilterFields`), so they never block a save;
    // Advanced JSON still refuses new ones (`recipeFromJSON`).
  }
  const strategy = String(def.strategy || '').trim()
  if (!strategy) {
    errors.strategy = 'Choose a pack type'
  } else {
    const descriptor = getStrategyDescriptor(strategy, serverStrategies)
    if (!descriptor) {
      errors.strategy = `Pack type "${strategy}" is no longer offered. Choose a listed pack type.`
    } else {
      const allowedLegCounts = getAllowedLegCounts(strategy, descriptor)

      // Leg counts validation
      const legCounts = Array.isArray(def.leg_counts) ? def.leg_counts : []
      if (!legCounts.length) {
        errors.leg_counts = 'At least one leg count must be selected'
      } else if (allowedLegCounts.length > 0) {
        const invalidCounts = legCounts.filter((c) => !allowedLegCounts.includes(c))
        if (invalidCounts.length) {
          errors.leg_counts = `Selected leg counts (${invalidCounts.join(', ')}) not permitted for ${descriptor?.label || strategy}. Allowed: ${allowedLegCounts.join(', ')}`
        }
      } else {
        errors.leg_counts = `No permitted leg counts defined for ${descriptor?.label || strategy}`
      }

      // Sports validation: derived strictly from descriptor.sports, compared
      // as canonical sports (a saved 'NFL' is football), as the server does.
      if (Array.isArray(descriptor.sports) && descriptor.sports.length > 0) {
        const allowedSports = descriptor.sports.map(canonicalSport)
        const sports = Array.isArray(def.sports) ? def.sports : []
        const invalidSports = sports.filter((s) => !allowedSports.includes(canonicalSport(s)))
        if (invalidSports.length) {
          errors.sports = `${descriptor.label || strategy} only supports ${descriptor.sports.join(', ')}. Invalid: ${invalidSports.join(', ')}`
        }
      }

      // Golf and motorsports offer Straight packs only (#688), as the server refuses.
      const straightOnly = straightOnlySports(def, descriptor, serverFilterOptions)
      if (straightOnly.length > 0) errors.strategy = straightOnlyReason(straightOnly)
    }
  }

  // Odds ordering validation: min_odds vs max_odds
  const minOdds = Number(def.min_odds) || 0
  const maxOdds = Number(def.max_odds) || 0
  if (minOdds !== 0 && maxOdds !== 0) {
    const minDec = americanToDecimal(minOdds)
    const maxDec = americanToDecimal(maxOdds)
    if (minDec > maxDec) {
      errors.odds = `Min odds (${formatAmericanOdds(minOdds)}) cannot be greater than max odds (${formatAmericanOdds(maxOdds)})`
    }
  }

  // Leg odds ordering validation: min_leg_odds vs max_leg_odds
  const minLegOdds = Number(def.min_leg_odds) || 0
  const maxLegOdds = Number(def.max_leg_odds) || 0
  if (minLegOdds !== 0 && maxLegOdds !== 0) {
    const minLegDec = americanToDecimal(minLegOdds)
    const maxLegDec = americanToDecimal(maxLegOdds)
    if (minLegDec > maxLegDec) {
      errors.leg_odds = `Min leg odds (${formatAmericanOdds(minLegOdds)}) cannot be greater than max leg odds (${formatAmericanOdds(maxLegOdds)})`
    }
  }

  // Lookahead hours
  const lookahead = Number(def.event_lookahead_hours)
  if (Number.isNaN(lookahead) || lookahead < 0) {
    errors.event_lookahead_hours = 'Event lookahead must be a non-negative number of hours'
  }

  // Start window: both bounds are wall-clock HH:MM and the window must not be reversed.
  // The messages name the start the way the Schedule section does ("earliest tip-off").
  const startTimeAfter = typeof def.start_time_after === 'string' ? def.start_time_after.trim() : ''
  const startTimeBefore = typeof def.start_time_before === 'string' ? def.start_time_before.trim() : ''
  const start = scheduleStartWord(def, getStrategyDescriptor(def.strategy, serverStrategies))
  if (startTimeAfter && !START_TIME_PATTERN.test(startTimeAfter)) {
    errors.start_time_after = `Enter the earliest ${start} as HH:MM (e.g. 13:00)`
  }
  if (startTimeBefore && !START_TIME_PATTERN.test(startTimeBefore)) {
    errors.start_time_before = `Enter the latest ${start} as HH:MM (e.g. 16:30)`
  }
  if (startTimeAfter && startTimeBefore && !errors.start_time_after && !errors.start_time_before && startTimeAfter > startTimeBefore) {
    errors.start_time_order = `The earliest ${start} cannot be later than the latest ${start}`
  }

  // Player selection: must be one the catalog advertises when it advertises any
  const playerSelection = typeof def.filters?.player_selection === 'string' ? def.filters.player_selection.trim() : ''
  if (playerSelection) {
    const offered = Array.isArray(serverFilterOptions?.player_selections)
      ? serverFilterOptions.player_selections.map((o) => String(o?.value ?? o).trim()).filter(Boolean)
      : []
    if (offered.length > 0 && !offered.includes(playerSelection)) {
      errors.player_selection = `Player selection ${playerSelection} is not offered by the catalog`
    }
  }

  // Filter line order validation
  const filterObj = def.filters || {}
  const minLine = filterObj.min_line != null && filterObj.min_line !== '' && Number.isFinite(Number(filterObj.min_line)) ? Number(filterObj.min_line) : null
  const maxLine = filterObj.max_line != null && filterObj.max_line !== '' && Number.isFinite(Number(filterObj.max_line)) ? Number(filterObj.max_line) : null
  if (minLine !== null && maxLine !== null && minLine > maxLine) {
    errors.filter_line = `Filter min line (${minLine}) cannot exceed max line (${maxLine})`
  }
  // Check include/exclude filter conflicts
  const conflicts = getFilterConflicts(filterObj)
  if (conflicts.length > 0) {
    errors.filter_conflicts = conflicts.join('. ')
  }

  // Outcome sides: must be strings, and must be offered by the catalog when it advertises them
  if (filterObj.outcome_roles != null) {
    const roles = filterObj.outcome_roles
    if (!Array.isArray(roles) || !roles.every((r) => typeof r === 'string' && r.trim())) {
      errors.outcome_roles = 'Outcome sides must be a list of side names'
    } else {
      const offered = Array.isArray(serverFilterOptions?.outcome_roles)
        ? serverFilterOptions.outcome_roles.map((o) => String(o?.value ?? o).trim().toLocaleLowerCase()).filter(Boolean)
        : []
      if (offered.length > 0) {
        const unknown = roles.filter((r) => !offered.includes(String(r).trim().toLocaleLowerCase()))
        if (unknown.length > 0) {
          errors.outcome_roles = `Outcome side ${unknown.join(', ')} is not offered by the catalog`
        }
      }
    }
  }
  if (filterObj.market_keys != null) {
    const keys = filterObj.market_keys
    if (!Array.isArray(keys) || keys.length > 50 || !keys.every((key) => typeof key === 'string' && key.trim())) {
      errors.market_keys = 'Select up to 50 specific markets'
    } else if (Array.isArray(serverFilterOptions?.market_keys)) {
      const offered = serverFilterOptions.market_keys.map((option) => option.value)
      if (keys.some((key) => !offered.includes(key.trim().toLocaleLowerCase()))) {
        errors.market_keys = 'A selected market is not offered by the catalog'
      }
    }
  }
  const marketPeriodsError = marketPeriodsProblem(filterObj.market_periods, serverFilterOptions)
  if (marketPeriodsError && !errors.market_keys) errors.market_keys = marketPeriodsError
  if (filterObj.periods != null && (!Array.isArray(filterObj.periods) || !filterObj.periods.every((id) => typeof id === 'string'))) {
    errors.market_keys = errors.market_keys || 'Periods must be a list of period names'
  }
  // Each selected market's own filters (#816), against its filter profile.
  Object.assign(errors, marketFilterProblems(filterObj, def, serverFilterOptions))
  // The schedule's teams (issue 1015): never in both lists, never out of scope.
  Object.assign(errors, teamFilterProblems(filterObj, def))
  Object.assign(errors, legMixProblems(def, getStrategyDescriptor(strategy, serverStrategies), serverFilterOptions))

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Converts recipe definition to pretty-printed JSON, losslessly but for the
 * per-market filters of markets no longer selected (#816), which the draft
 * keeps only for Undo and the save drops.
 */
export function recipeToJSON(recipe) {
  if (!recipe?.definition) return '{}'
  const { definition } = recipe
  if (!isPlainObject(definition.filters) || !Object.hasOwn(definition.filters, 'market_filters')) return JSON.stringify(definition, null, 2)
  const filters = { ...definition.filters, market_filters: selectedMarketFilters(definition.filters) }
  if (filters.market_filters === undefined) delete filters.market_filters
  return JSON.stringify({ ...definition, filters }, null, 2)
}

/**
 * Parses JSON into recipe definition, preserving top-level recipe fields.
 * `options.filterOptions` is the server's catalog: without filter profiles
 * (an older server, `supportsMarketFilters`) a `market_filters` entry the
 * draft does not already hold is refused, since that server refuses the
 * field; omitted, the check is skipped. An entry the draft holds for a
 * market the JSON does not select is dropped, as the save would drop it.
 */
export function recipeFromJSON(jsonString, currentRecipe = {}, options = {}) {
  try {
    const parsed = JSON.parse(jsonString)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { recipe: currentRecipe, error: 'JSON root must be an object' }
    }
    const unknownDefKeys = Object.keys(parsed).filter((k) => !KNOWN_DEFINITION_KEYS.includes(k))
    if (unknownDefKeys.length > 0) {
      return {
        recipe: currentRecipe,
        error: `Unknown definition field(s): ${unknownDefKeys.join(', ')}. Unsupported fields are not permitted.`,
      }
    }

    if (parsed.version != null && (!Number.isInteger(parsed.version) || parsed.version < 1)) {
      return { recipe: currentRecipe, error: 'version must be a positive integer' }
    }
    if (parsed.strategy != null && typeof parsed.strategy !== 'string') {
      return { recipe: currentRecipe, error: 'strategy must be a string' }
    }
    if (parsed.bet_type != null && typeof parsed.bet_type !== 'string') {
      return { recipe: currentRecipe, error: 'bet_type must be a string' }
    }
    if (parsed.sports != null && (!Array.isArray(parsed.sports) || !parsed.sports.every((s) => typeof s === 'string'))) {
      return { recipe: currentRecipe, error: 'sports must be an array of strings' }
    }
    if (parsed.leagues != null && (!Array.isArray(parsed.leagues) || !parsed.leagues.every((s) => typeof s === 'string'))) {
      return { recipe: currentRecipe, error: 'leagues must be an array of strings' }
    }
    if (parsed.leg_counts != null && (!Array.isArray(parsed.leg_counts) || !parsed.leg_counts.every((n) => Number.isInteger(n) && n > 0))) {
      return { recipe: currentRecipe, error: 'leg_counts must be an array of positive integers' }
    }
    if (parsed.min_odds != null && !Number.isInteger(parsed.min_odds)) {
      return { recipe: currentRecipe, error: 'min_odds must be an integer' }
    }
    if (parsed.max_odds != null && !Number.isInteger(parsed.max_odds)) {
      return { recipe: currentRecipe, error: 'max_odds must be an integer' }
    }
    if (parsed.min_leg_odds != null && !Number.isInteger(parsed.min_leg_odds)) {
      return { recipe: currentRecipe, error: 'min_leg_odds must be an integer' }
    }
    if (parsed.max_leg_odds != null && !Number.isInteger(parsed.max_leg_odds)) {
      return { recipe: currentRecipe, error: 'max_leg_odds must be an integer' }
    }
    if (parsed.event_lookahead_hours != null && (!Number.isInteger(parsed.event_lookahead_hours) || parsed.event_lookahead_hours < 0)) {
      return { recipe: currentRecipe, error: 'event_lookahead_hours must be a non-negative integer' }
    }

    if (parsed.filters != null) {
      if (typeof parsed.filters !== 'object' || Array.isArray(parsed.filters)) {
        return { recipe: currentRecipe, error: 'filters must be an object' }
      }
      // A field the schedule already had (from a newer server) may stay.
      const storedFilters = currentRecipe?.definition?.filters || {}
      const unknownFilterKeys = Object.keys(parsed.filters).filter((k) => !KNOWN_FILTER_KEYS.includes(k) && !Object.hasOwn(storedFilters, k))
      if (unknownFilterKeys.length > 0) {
        return {
          recipe: currentRecipe,
          error: `Unknown filter field(s): ${unknownFilterKeys.join(', ')}. Unsupported fields are not permitted.`,
        }
      }

      const stringArrayKeys = [
        'market_families',
        'market_keys',
        'excluded_market_families',
        'stat_categories',
        'participants',
        'excluded_participants',
        'outcome_roles',
      ]
      for (const key of stringArrayKeys) {
        if (parsed.filters[key] != null && (!Array.isArray(parsed.filters[key]) || !parsed.filters[key].every((s) => typeof s === 'string'))) {
          return { recipe: currentRecipe, error: `filters.${key} must be an array of strings` }
        }
      }

      const mix = parsed.filters.leg_mix
      if (mix != null && (!Array.isArray(mix) || !mix.every((row) => isPlainObject(row)
        && typeof row.market_key === 'string' && row.market_key.trim()
        && Number.isInteger(row.count) && row.count >= 1
        && (row.period == null || typeof row.period === 'string')
        && (row.same_player_as == null || typeof row.same_player_as === 'string')
        && (row.same_team_as == null || typeof row.same_team_as === 'string')))) {
        return { recipe: currentRecipe, error: 'Legs per market needs a market, period and positive whole count per row' }
      }

      // Teams are objects with a name (issue 1015); a bare name, the shape
      // participants take, would be dropped on save, so it is refused here.
      for (const key of ['teams', 'excluded_teams']) {
        const refs = parsed.filters[key]
        if (refs != null && (!Array.isArray(refs) || !refs.every((ref) => isPlainObject(ref) && typeof ref.name === 'string' && ref.name.trim()))) {
          return { recipe: currentRecipe, error: `filters.${key} must be an array of teams, each with a name` }
        }
      }

      if (parsed.filters.periods != null && (!Array.isArray(parsed.filters.periods) || !parsed.filters.periods.every((s) => typeof s === 'string'))) {
        return { recipe: currentRecipe, error: 'filters.periods must be an array of strings' }
      }
      const marketPeriods = parsed.filters.market_periods
      if (marketPeriods != null && (typeof marketPeriods !== 'object' || Array.isArray(marketPeriods)
        || !Object.values(marketPeriods).every((ids) => Array.isArray(ids) && ids.every((s) => typeof s === 'string')))) {
        return { recipe: currentRecipe, error: 'filters.market_periods must map each market key to an array of strings' }
      }
      if (parsed.filters.market_filters != null) {
        const held = currentRecipe?.definition?.filters?.market_filters
        const problem = marketFiltersJsonProblem(parsed.filters.market_filters, parsed.filters.market_keys, {
          heldKeys: new Set(isPlainObject(held) ? Object.keys(held).map(lowerKey) : []),
          supported: options?.filterOptions === undefined || supportsMarketFilters(options.filterOptions),
        })
        if (problem) return { recipe: currentRecipe, error: problem }
        parsed.filters.market_filters = selectedMarketFilters(parsed.filters)
      }

      if (parsed.filters.main_lines_only != null && typeof parsed.filters.main_lines_only !== 'boolean') {
        return { recipe: currentRecipe, error: 'filters.main_lines_only must be a boolean' }
      }

      if (parsed.filters.min_line != null && (typeof parsed.filters.min_line !== 'number' || !Number.isFinite(parsed.filters.min_line))) {
        return { recipe: currentRecipe, error: 'filters.min_line must be a finite number or null' }
      }

      if (parsed.filters.max_line != null && (typeof parsed.filters.max_line !== 'number' || !Number.isFinite(parsed.filters.max_line))) {
        return { recipe: currentRecipe, error: 'filters.max_line must be a finite number or null' }
      }

      if (parsed.filters.min_line != null && parsed.filters.max_line != null && parsed.filters.min_line > parsed.filters.max_line) {
        return { recipe: currentRecipe, error: `Filter min line (${parsed.filters.min_line}) cannot exceed max line (${parsed.filters.max_line})` }
      }
    }

    const merged = {
      ...currentRecipe,
      definition: cloneDefinition(parsed),
    }
    if (currentRecipe?.definition?.filters?.leg_mix?.length
      && (!supportsLegMix(parsed) || parsed.filters?.market_families?.length)) {
      merged.definition = withLegMix(merged.definition, [])
    }
    return { recipe: merged, error: null }
  } catch (err) {
    return { recipe: currentRecipe, error: err.message || 'Invalid JSON syntax' }
  }
}
